import { useMemo, useState } from 'react';
import { ChevronDown, Save } from 'lucide-react';
import { I18n } from '@/i18n/I18n';
import { cn } from '@/frontend/lib/utils';
import { evaluateReportRuntime } from '@/frontend/reports/loader';
import { CompiledReportBundle, REPORT_BUNDLE_FIXTURE_DIR, isValidBundlePath } from '@/reports/bundles/types';
import { judgeReport } from '@/reports/report';
import { InputsReportContext, ReportVerdict, RunReportContext } from '@/reports/types';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../ui/dropdown-menu';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { baseName } from './designerChrome';

export interface DraftVerdict {
  verdict: ReportVerdict;
  label: string;
  /** the first findings that did not pass, to say why */
  reasons: string[];
}

const VERDICTS: ReportVerdict[] = ['passed', 'failed', 'incomplete'];

/** What the draft, as last saved, comes to over a context: the verdict it prints, and why. Null while it does not compile, or throws. */
export function useDraftVerdict(compiled: CompiledReportBundle | null, context: RunReportContext | InputsReportContext | null, values?: Record<string, unknown>): DraftVerdict | null {
  // the scripts run once per compile; what is typed only evaluates them again
  const module = useMemo(() => {
    if (!compiled) return null;
    try {
      return evaluateReportRuntime({ bundleVersionId: `draft:${compiled.hash}`, key: compiled.key, contractVersion: compiled.contractVersion, definitionJs: compiled.definitionJs, documentJs: compiled.documentJs, hash: compiled.hash });
    } catch {
      return null;
    }
  }, [compiled?.hash]);
  return useMemo(() => {
    if (!module || !context) return null;
    try {
      const { definition } = module;
      // the verdict of record, as the server judges a fixture saved from here
      const model = judgeReport(definition, definition.normalizeValues(values ?? definition.defaultValues()), context as RunReportContext);
      return { verdict: model.verdict, label: model.verdictLabel, reasons: model.findings.filter((f) => !f.passed).slice(0, 2).map((f) => f.message) };
    } catch {
      return null;
    }
  }, [module, context, values]);
}

const VERDICT_TONE: Record<ReportVerdict, string> = {
  passed: 'border-emerald-300 bg-emerald-50 text-emerald-900',
  failed: 'border-red-300 bg-red-50 text-red-800',
  incomplete: 'border-amber-300 bg-amber-50 text-amber-900',
};

export function VerdictChip({ verdict }: { verdict: DraftVerdict }) {
  return <span className={cn('inline-flex h-5 shrink-0 items-center rounded-sm border-[1px] px-1.5 text-[0.7rem] font-bold tracking-wide', VERDICT_TONE[verdict.verdict])}>{verdict.label}</span>;
}

/**
 * Under the form in Try it: what the report comes to with what is typed, and
 * the way to keep it — as a fixture that must come to that from now on. A
 * fixture can be started from, too.
 */
export function TryItBar({ verdict, fixtures, existing, onStartFrom, onClear, onSave }: {
  verdict: DraftVerdict | null;
  fixtures: string[];
  /** every file in the bundle, so a new fixture is never saved over one */
  existing: string[];
  onStartFrom: (path: string) => void;
  onClear: () => void;
  onSave: (path: string, verdict: ReportVerdict) => void;
}) {
  const [saving, setSaving] = useState(false);
  return (
    <div className="flex min-h-11 shrink-0 flex-wrap items-center gap-x-3 gap-y-1.5 border-t bg-background px-3 py-1.5 text-xs">
      <span className="flex min-w-0 flex-1 items-center gap-2">
        <span className="shrink-0 whitespace-nowrap text-muted-foreground">{I18n.get('designerComesTo')}</span>
        {verdict ? <VerdictChip verdict={verdict} /> : <span className="text-muted-foreground">{I18n.get('designerComesToUnknown')}</span>}
        {verdict && verdict.reasons.length > 0 && <span className="truncate text-muted-foreground" title={verdict.reasons.join(' · ')}>· {verdict.reasons.join(' · ')}</span>}
      </span>
      {fixtures.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger render={<Button variant="ghost" size="xs" />}>{I18n.get('designerStartFromFixture')} <ChevronDown /></DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {fixtures.map((path) => <DropdownMenuItem key={path} className="font-mono text-xs" onClick={() => onStartFrom(path)}>{baseName(path)}</DropdownMenuItem>)}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      <Button variant="ghost" size="xs" onClick={onClear}>{I18n.get('designerClear')}</Button>
      <Button variant="outline" size="xs" onClick={() => setSaving(true)} disabled={!verdict}><Save /> {I18n.get('designerSaveAsFixture')}</Button>
      {saving && verdict && <SaveFixtureDialog verdict={verdict} existing={existing} onHide={() => setSaving(false)} onSave={(path, expected) => { setSaving(false); onSave(path, expected); }} />}
    </div>
  );
}

const slug = (text: string) => text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

function SaveFixtureDialog({ verdict, existing, onHide, onSave }: { verdict: DraftVerdict; existing: string[]; onHide: () => void; onSave: (path: string, verdict: ReportVerdict) => void }) {
  const [name, setName] = useState(() => {
    const base = slug(verdict.label) || verdict.verdict;
    let n = 1;
    while (existing.includes(`${REPORT_BUNDLE_FIXTURE_DIR}${base}${n === 1 ? '' : `-${n}`}.json`)) n += 1;
    return n === 1 ? base : `${base}-${n}`;
  });
  const [expected, setExpected] = useState<ReportVerdict>(verdict.verdict);
  const path = `${REPORT_BUNDLE_FIXTURE_DIR}${name}.json`;
  const taken = existing.includes(path);
  const valid = name !== '' && isValidBundlePath(path) && !taken;
  const submit = () => { if (valid) onSave(path, expected); };
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onHide(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{I18n.get('designerSaveAsFixtureTitle')}</DialogTitle>
          <DialogDescription>{I18n.get('designerSaveAsFixtureHelp')}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-2">
          <Label htmlFor="fixture-name">{I18n.get('designerFixtureFile')}</Label>
          <div className="flex items-center gap-1 font-mono text-sm">
            <span className="text-muted-foreground">{REPORT_BUNDLE_FIXTURE_DIR}</span>
            <Input id="fixture-name" className="font-mono" value={name} aria-invalid={name !== '' && !valid} onChange={(e) => setName(e.target.value.replace(/[^A-Za-z0-9_-]+/g, '-'))} onKeyDown={(e) => { if (e.key === 'Enter') submit(); }} />
            <span className="text-muted-foreground">.json</span>
          </div>
          {taken && <p className="text-xs text-destructive">{I18n.get('designerFixtureTaken')}</p>}
        </div>
        <fieldset className="grid gap-1.5">
          <legend className="float-none mb-1.5 w-auto p-0 text-sm font-medium">{I18n.get('designerFixtureMustComeTo')}</legend>
          {[verdict.verdict, ...VERDICTS.filter((v) => v !== verdict.verdict)].map((v) => (
            <label key={v} className="flex items-center gap-2 text-sm">
              <input type="radio" name="fixture-expect" value={v} checked={expected === v} onChange={() => setExpected(v)} />
              {v === verdict.verdict
                ? I18n.get('designerExpectAsNow', { label: verdict.label })
                : I18n.get('designerExpectOther', { verdict: I18n.get(`verdict_${v}`) })}
            </label>
          ))}
        </fieldset>
        <DialogFooter>
          <Button variant="outline" onClick={onHide}>{I18n.get('cancel')}</Button>
          <Button disabled={!valid} onClick={submit}>{I18n.get('designerSaveAndOpen')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
